import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SITE } from "@/config";
import { PrimaryCTA, GhostCTA } from "@/components/CTA";

// Animazioni al mount (non legate allo scroll): la pagina è sempre visibile immediatamente.
const Fade = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Kicker = ({ label, dark = false }) => (
  <Fade
    className={`mb-10 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] ${
      dark ? "text-white/45" : "text-ink/45"
    }`}
  >
    <span className={dark ? "text-accent" : "text-accent-dark"}>—</span>
    <span className={`h-px w-10 ${dark ? "bg-white/20" : "bg-ink/20"}`} aria-hidden="true" />
    <span>{label}</span>
  </Fade>
);

const PATH = ["PERSONE", "ORGANIZZAZIONE", "NUMERI", "MARKETING", "CRESCITA", "LIBERTÀ"];

const GOALS = [
  "Voglio vedere un imprenditore recuperare la propria efficienza emotiva.",
  "Voglio vedere un team diventare più responsabile e autonomo.",
  "Voglio vedere crescere fatturato e utile.",
  "Voglio vedere l'imprenditore imparare a delegare.",
];

const AboutPage = () => (
  <div data-testid="about-page" className="bg-fog pt-[72px] text-ink">
    <div className="mx-auto max-w-[1440px] px-6 py-20 lg:px-12 lg:py-32">
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Fade>
            <div className="relative lg:sticky lg:top-28">
              <div className="absolute -left-4 -top-4 h-full w-full border border-accent-dark/30" aria-hidden="true" />
              <img
                src="/assets/solo-hero.jpg"
                alt="Mattia Cosimo Saracino — Consulente Aziendale"
                data-testid="about-page-photo"
                className="relative aspect-[3/4] w-full object-cover object-top"
              />
            </div>
          </Fade>
        </div>
        <div className="lg:col-span-7">
          <Kicker label="Chi sono" />
          <Fade>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
              Prima delle aziende, vengono le persone.
            </h1>
            <p className="mt-10 font-display text-2xl font-bold tracking-tight">Mattia Cosimo Saracino</p>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.3em] text-accent-dark">
              Consulente Aziendale
            </p>
          </Fade>
          <Fade delay={0.1}>
            <div className="mt-10 max-w-xl space-y-6 text-base leading-relaxed text-ink/65 md:text-lg">
              <p>
                Mattia Cosimo Saracino proviene dal mondo del marketing e della direzione esecutiva
                nel settore dello spettacolo, dove ha collaborato con realtà come Rai, Mediaset e
                Battiti Live: ambienti fatti di persone, organizzazione, pressione, risultati e
                grandi progetti.
              </p>
              <p>
                Successivamente ha conosciuto OSM, inizialmente come cliente. L'approccio incontrato
                gli ha fatto scoprire una visione dell'impresa che mette al centro, prima di tutto,
                la persona: la sua energia, la gestione emotiva, le capacità, le potenzialità.
              </p>
              <p>Questo incontro ha contribuito alla scelta di dedicarsi alla consulenza aziendale.</p>
            </div>
          </Fade>
          <Fade delay={0.15} className="mt-12 grid grid-cols-2 gap-4">
            <img
              src="/assets/osm-mare.jpg"
              alt="Mattia Cosimo Saracino durante un confronto — percorso OSM"
              loading="lazy"
              data-testid="about-photo-osm"
              className="aspect-[4/5] w-full border border-ink/10 object-cover"
            />
            <img
              src="/assets/duo-osm.jpg"
              alt="Mattia Cosimo Saracino a un evento OSM"
              loading="lazy"
              data-testid="about-photo-evento"
              className="aspect-[4/5] w-full border border-ink/10 object-cover"
            />
          </Fade>
        </div>
      </div>
    </div>

    <section data-testid="father-story" className="bg-ink py-28 text-white lg:py-40">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <Kicker label="La mia storia" dark />
        <Fade>
          <h2 className="max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tighter sm:text-5xl">
            Perché proprio gli imprenditori?
          </h2>
        </Fade>
        <Fade delay={0.1}>
          <div className="mt-10 max-w-2xl space-y-6 text-base leading-relaxed text-white/65 md:text-lg">
            <p>La risposta viene da molto prima della mia carriera. Viene da mio padre.</p>
            <p>
              Per trent'anni è stato titolare di uno storico panificio di Mesagne. Per me è sempre
              stato — ed è ancora — il mio supereroe. Un grande uomo e un grande lavoratore.
            </p>
            <p>
              Ma da bambino ricordo anche un'altra cosa. Lo vedevo uscire di casa alle 2 di notte e,
              alcune volte, tornare alle 21. L'azienda funzionava. Ma dipendeva enormemente da lui.
              Non era riuscito a costruire una delega che gli permettesse di staccarsi realmente
              dalla sua attività.
            </p>
            <p>Crescendo ho capito una cosa:</p>
          </div>
        </Fade>
        <Fade delay={0.15}>
          <p className="mt-16 max-w-5xl font-display text-[clamp(2rem,5.5vw,4.4rem)] font-extrabold leading-[1.05] tracking-tighter">
            Un'azienda può avere <span className="text-accent">successo</span> e allo stesso tempo
            toglierti la <span className="text-accent">libertà</span>.
          </p>
        </Fade>
        <Fade delay={0.2}>
          <div className="mt-16 max-w-2xl space-y-6 text-base leading-relaxed text-white/65 md:text-lg">
            <p>
              Una parte importante del mio lavoro nasce proprio da qui. Non voglio che un altro padre
              debba scegliere, anche inconsapevolmente, tra far funzionare la propria azienda e avere
              il tempo di vivere suo figlio.
            </p>
            <p>
              Perché il vero successo di un imprenditore non può essere misurato soltanto da quanto
              fattura. Deve essere misurato anche da quanto è libero di vivere ciò che quel fatturato
              dovrebbe permettergli di vivere.
            </p>
          </div>
        </Fade>
      </div>
    </section>

    <section data-testid="bf360-bridge" className="relative overflow-hidden bg-anthracite py-28 text-white lg:py-36">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(212,175,55,0.08),transparent_55%)]" />
      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-12">
        <Fade>
          <p className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">E la tua?</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <h2 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
              BUSINESS FREEDOM 360
            </h2>
            <span className="border border-accent px-3 py-1.5 font-mono text-[11px] tracking-[0.3em] text-accent">
              BF360
            </span>
          </div>
          <p className="mt-8 max-w-xl font-display text-2xl font-bold leading-snug tracking-tight text-white/90 md:text-3xl">
            Quanto sei realmente libero dalla tua azienda?
          </p>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/60 md:text-lg">
            BF360 analizza diverse aree della tua attività per aiutarti a comprendere quanto
            l'azienda sia realmente autonoma e quanto dipenda ancora dalla tua presenza.
          </p>
        </Fade>
        <Fade delay={0.15} className="mt-12">
          <PrimaryCTA href={SITE.bf360} testid="about-bf360-cta" className="px-10 py-5">
            Scopri il tuo indice di libertà
          </PrimaryCTA>
        </Fade>
      </div>
    </section>

    <section className="bg-fog py-28 lg:py-36">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <Kicker label="Il mio modo di fare consulenza" />
        <Fade>
          <h2 className="max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tighter sm:text-5xl">
            Non entro in azienda per dirti cosa fare.
          </h2>
          <div className="mt-10 max-w-2xl text-base leading-relaxed text-ink/65 md:text-lg">
            <p>
              Quando entro in un'azienda non parto dal presupposto di avere qualcosa da insegnare
              all'imprenditore. Spesso le capacità necessarie per fare il salto successivo sono già
              lì. Il mio compito è creare un percorso capace di farle emergere, trasformarle in
              azioni e accompagnare l'imprenditore nell'applicarle.
            </p>
          </div>
        </Fade>
        <Fade delay={0.1}>
          <p className="mt-14 border-l-2 border-accent-dark pl-6 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
            LA CONSULENZA NON FINISCE IN AULA.
          </p>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/65 md:text-lg">
            Entro direttamente in azienda. Lavoro con l'imprenditore. Lavoro con le persone. Analizzo
            ciò che sta accadendo. Costruiamo strategie, strumenti e metodi. E rimango al fianco
            dell'imprenditore nel percorso verso gli obiettivi che abbiamo definito.
          </p>
        </Fade>
        <Fade delay={0.15} className="mt-14 flex flex-wrap items-center gap-3" data-testid="about-path">
          {PATH.map((p, i) => (
            <span key={p} className="flex items-center gap-3">
              <span
                className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] ${
                  p === "LIBERTÀ" ? "border-accent-dark/50 text-accent-dark" : "border-ink/20 text-ink/60"
                }`}
              >
                {p}
              </span>
              {i < PATH.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-ink/30" aria-hidden="true" />}
            </span>
          ))}
        </Fade>
      </div>
    </section>

    <section className="border-t border-ink/10 bg-fog py-28 lg:py-36">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <Kicker label="Cosa voglio ottenere" />
        <Fade>
          <h2 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
            Il risultato che cerco.
          </h2>
          <p className="mt-10 max-w-2xl text-base leading-relaxed text-ink/65 md:text-lg">
            Non mi interessa semplicemente vedere un grafico salire.
          </p>
          <ul className="mt-8 max-w-2xl space-y-4">
            {GOALS.map((g) => (
              <li key={g} className="flex items-baseline gap-3 text-base leading-relaxed text-ink/70 md:text-lg">
                <span className="text-accent-dark" aria-hidden="true">—</span>
                {g}
              </li>
            ))}
          </ul>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/65 md:text-lg">
            E progressivamente costruire un'azienda capace di funzionare senza dipendere completamente
            dalla sua presenza. Fino ad arrivare a utilizzare ciò che l'azienda genera per costruire
            libertà e patrimonio anche al di fuori dell'azienda.
          </p>
        </Fade>
      </div>
    </section>

    <section data-testid="signature-quote" className="bg-ink py-28 text-white lg:py-40">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <Fade>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-accent">Il mio manifesto</p>
          <blockquote className="mt-10 max-w-4xl border-l-2 border-accent pl-8 font-display text-3xl font-extrabold leading-[1.15] tracking-tighter sm:text-4xl lg:text-5xl">
            "Quando entro in un'azienda, il mio obiettivo non è trovare un nuovo cliente, ma scegliere
            un nuovo <span className="text-accent">alleato</span>."
          </blockquote>
          <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.3em] text-white/45">
            — Mattia Cosimo Saracino
          </p>
        </Fade>
        <Fade delay={0.15} className="mt-16 flex flex-wrap gap-4">
          <PrimaryCTA href={SITE.bf360} testid="about-page-cta" />
          <GhostCTA to="/contatti" dark testid="about-page-contact">
            Parliamo della tua azienda
          </GhostCTA>
        </Fade>
      </div>
    </section>
  </div>
);

export default AboutPage;
